/**
 * Create Request Dashboard (User V2)
 * ===================================
 * 
 * Product selection and cart system for creating procurement requests.
 * Users can browse products, add to cart, and submit requests.
 * 
 * Workflow:
 * 1. User browses available products
 * 2. User adds products to cart
 * 3. User reviews cart and submits request
 * 
 * Features:
 * - Product browsing with DataDashboard
 * - Shopping cart functionality
 * - Simple request submission
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import DataDashboard from '../../../common/DataDashboard';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getCategoryName } from '../../../../utils/categories';
import Toast from '../../../Toast';
import './CreateRequestDashboard.scss';

interface CreateRequestDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const CreateRequestDashboard: React.FC<CreateRequestDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();

  // Cart state
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [showCart, setShowCart] = useState(false);

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');

  // Products list (from API)
  const [availableProducts, setAvailableProducts] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false,
    sort_by: 'name',
    sort_order: 'asc'
  });
  const [productCache, setProductCache] = useState<Record<string, any>>({});

  type ProductFetchParams = {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    columnFilters?: Record<string, string>;
  };

  const lastFetchParamsRef = useRef<ProductFetchParams>({
    page: 1,
    limit: 25,
    search: '',
    sortBy: 'name',
    sortOrder: 'asc',
    columnFilters: {}
  });

  const updateProductCache = useCallback((products: any[]) => {
    if (!products || products.length === 0) {
      return;
    }

    setProductCache(prevCache => {
      const nextCache = { ...prevCache };
      products.forEach(product => {
        if (product && product._id) {
          nextCache[product._id] = product;
        }
      });
      return nextCache;
    });
  }, []);

  const loadProducts = useCallback(async (params: ProductFetchParams = {}) => {
    if (!token) {
      return;
    }

    const {
      page = lastFetchParamsRef.current.page || 1,
      limit = lastFetchParamsRef.current.limit || 25,
      search = lastFetchParamsRef.current.search || '',
      sortBy = lastFetchParamsRef.current.sortBy || 'name',
      sortOrder = lastFetchParamsRef.current.sortOrder || 'asc',
      columnFilters = lastFetchParamsRef.current.columnFilters || {}
    } = params;

    const normalizedFilters = Object.entries(columnFilters || {}).reduce(
      (acc, [key, value]) => {
        const trimmed = typeof value === 'string' ? value.trim() : '';
        if (trimmed) {
          acc[key] = trimmed;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    lastFetchParamsRef.current = {
      page,
      limit,
      search,
      sortBy,
      sortOrder,
      columnFilters: normalizedFilters
    };

    setIsLoadingProducts(true);
    setProductsError(null);

    try {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
        region: 'tr',
        company_id: user.company_id || ''
      });

      if (search) {
        queryParams.append('search', search);
      }

      Object.entries(normalizedFilters).forEach(([key, value]) => {
        queryParams.append(`filter_${key}`, value);
      });

      console.log('🔍 DEBUG: Fetching products with params:', {
        page,
        limit,
        search,
        sortBy,
        sortOrder,
        columnFilters: normalizedFilters,
        url: `${API_ENDPOINTS.PRODUCTS_GROUPED}?${queryParams.toString()}`
      });

      const response = await fetch(
        `${API_ENDPOINTS.PRODUCTS_GROUPED}?${queryParams.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const responseData = await response.json();
      if (!responseData.success) {
        throw new Error(responseData.error || responseData.message || 'Failed to load products');
      }

      const products = responseData.data || [];
      setAvailableProducts(products);
      updateProductCache(products);

      const pagination = responseData.pagination || {};
      const total = pagination.total ?? products.length;
      const limitValue = pagination.limit ?? limit;
      const pages = pagination.pages ?? (limitValue ? Math.ceil(total / limitValue) : 0);
      const currentPage = pagination.page ?? page;

      setPaginationInfo({
        page: currentPage,
        limit: limitValue,
        total,
        pages,
        has_next: pagination.has_next ?? currentPage < pages,
        has_prev: pagination.has_prev ?? currentPage > 1,
        sort_by: pagination.sort_by ?? sortBy,
        sort_order: pagination.sort_order ?? sortOrder
      });
    } catch (error) {
      console.error('Error fetching products:', error);
      setAvailableProducts([]);
      setProductsError(error instanceof Error ? error.message : 'Failed to load products');
      setPaginationInfo(prev => ({
        ...prev,
        page,
        limit,
        total: 0,
        pages: 0,
        has_next: false,
        has_prev: page > 1,
        sort_by: sortBy,
        sort_order: sortOrder
      }));
    } finally {
      setIsLoadingProducts(false);
    }
  }, [token, updateProductCache]);

  const handleDataFetch = useCallback((params: ProductFetchParams) => {
    const nextParams = params || {};
    loadProducts({
      page: nextParams.page,
      limit: nextParams.limit,
      search: nextParams.search,
      sortBy: nextParams.sortBy,
      sortOrder: nextParams.sortOrder,
      columnFilters: nextParams.columnFilters
    });
  }, [loadProducts]);

  useEffect(() => {
    if (token) {
      loadProducts({
        page: 1,
        limit: lastFetchParamsRef.current.limit,
        search: '',
        sortBy: 'name',
        sortOrder: 'asc',
        columnFilters: {}
      });
    }
  }, [token, loadProducts]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Variant selection state
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [productVariants, setProductVariants] = useState<any[]>([]);
  const [loadingVariants, setLoadingVariants] = useState(false);

  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState<string>('');
  const [selectedInvoiceAddress, setSelectedInvoiceAddress] = useState<string>('');

  // Load cart items on component mount
  useEffect(() => {
    if (token && user._id) {
      loadCartItems();
    }
  }, [token, user._id]);

  // Load company addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!token || !user.company_id) return;

      try {
        const response = await fetch(
          API_ENDPOINTS.COMPANY_ADDRESSES(user.company_id),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setAddresses(data.data);
            // Set default addresses if available
            const defaultAddress = data.data.find((addr: any) => addr.is_default);
            if (defaultAddress) {
              setSelectedDeliveryAddress(defaultAddress._id);
              setSelectedInvoiceAddress(defaultAddress._id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
      }
    };

    fetchAddresses();
  }, [token, user.company_id]);

  // Add product to cart
  const handleAddToCart = async (product: any) => {
    // Check if product has variants
    if (product.variant_count && product.variant_count > 1) {
      if (!product.variant_id) {
        console.warn('⚠️ Product reports variants but is missing variant_id. Falling back to direct add.', product);
      } else {
        console.log('🔍 Product has variants, opening variant selector:', product.variant_count);
        setSelectedProduct(product);
        setShowVariantModal(true);
        // Fetch variants
        await fetchProductVariants(product.variant_id);
        return;
      }
    }

    // No variants or only 1 variant, add directly
    await addProductToCart(product);
  };

  // Fetch all variants of a product
  const fetchProductVariants = async (variantId?: string) => {
    if (!variantId) {
      console.warn('⚠️ fetchProductVariants called without a valid variantId');
      setProductVariants([]);
      return;
    }

    try {
      setLoadingVariants(true);
      const response = await fetch(
        API_ENDPOINTS.PRODUCT_VARIANTS(variantId),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setProductVariants(data.data);
          updateProductCache(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching variants:', error);
    } finally {
      setLoadingVariants(false);
    }
  };

  // Add specific product to cart (used by both direct add and variant selection)
  const addProductToCart = async (product: any) => {
    try {
      if (product && product._id) {
        updateProductCache([product]);
      }
      console.log('🛒 Adding product to cart:', {
        product_id: product._id,
        product_name: product.name,
        user_id: user._id,
        company_id: user.company_id
      });

      const response = await fetch(API_ENDPOINTS.PREREQUEST_ADD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user._id,
          'X-Company-ID': user.company_id
        },
        body: JSON.stringify({
          product_id: product._id,
          quantity: 1
        })
      });

      console.log('🛒 Add to cart response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Product added to cart successfully:', data);

        // Refresh cart items
        await loadCartItems();

        // Show success toast
        setToastMessage('Product added to cart successfully!');
        setToastType('success');
        setShowToast(true);
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to add to cart:', errorData);

        // Show error toast
        setToastMessage(`Failed to add to cart: ${errorData.message || 'Unknown error'}`);
        setToastType('error');
        setShowToast(true);
      }
    } catch (error) {
      console.error('❌ Error adding to cart:', error);

      // Show error toast
      setToastMessage('Error adding to cart. Please try again.');
      setToastType('error');
      setShowToast(true);
    }
  };

  // Load cart items from API
  const loadCartItems = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PREREQUESTS, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user._id
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCartItems(data.data.items || []);
        }
      }
    } catch (error) {
      console.error('Error loading cart items:', error);
    }
  };

  // Remove product from cart
  const handleRemoveFromCart = async (productId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.PREREQUEST_REMOVE, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user._id
        },
        body: JSON.stringify({
          product_id: productId
        })
      });

      if (response.ok) {
        await loadCartItems();
        console.log('Product removed from cart successfully');
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  // Update cart item quantity
  const handleUpdateCartQuantity = async (productId: string, quantity: number) => {
    try {
      const response = await fetch(API_ENDPOINTS.PREREQUEST_UPDATE, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user._id
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: quantity
        })
      });

      if (response.ok) {
        await loadCartItems();
        console.log('Cart quantity updated successfully');
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error);
    }
  };

  // Clear cart
  const handleClearCart = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PREREQUEST_CLEAR, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-User-ID': user._id
        }
      });

      if (response.ok) {
        setCartItems([]);
        console.log('Cart cleared successfully');
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  // Submit request
  const handleSubmitRequest = async () => {
    if (cartItems.length === 0) {
      alert('Please add products to cart before submitting');
      return;
    }

    if (!selectedDeliveryAddress || !selectedInvoiceAddress) {
      alert('Please select both delivery and invoice addresses');
      return;
    }

    setIsSubmitting(true);

    try {
      // Find selected addresses
      const deliveryAddress = addresses.find(addr => addr._id === selectedDeliveryAddress);
      const invoiceAddress = addresses.find(addr => addr._id === selectedInvoiceAddress);

      // Format address as string
      const formatAddress = (addr: any) => {
        if (!addr) return '';
        return `${addr.street}, ${addr.city}${addr.state ? ', ' + addr.state : ''} ${addr.postal_code}, ${addr.country}`;
      };

      // Generate title from product names (same as CartPage)
      const productNames = cartItems.map(item => item.product_name).filter(Boolean) || [];
      const title = productNames.length > 0
        ? productNames.join(', ')
        : `İstek - ${new Date().toLocaleDateString('tr-TR')}`;

      // Create basic request
      const requestPayload = {
        title: title,
        delivery_address: formatAddress(deliveryAddress),
        invoice_address: formatAddress(invoiceAddress),
        contact_person: user.name || 'User',
        contact_email: user.email || 'user@example.com',
        contact_phone: user.phone || ''
      };

      console.log('Creating request with payload:', requestPayload);

      const requestResponse = await fetch(`${API_ENDPOINTS.REQUESTS_V2}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!requestResponse.ok) {
        const errorData = await requestResponse.json();
        throw new Error(errorData.message || 'Failed to create request');
      }

      const requestResult = await requestResponse.json();
      const requestId = requestResult.data._id;

      console.log('Request created successfully:', requestResult);

      // Add products to request
      for (let i = 0; i < cartItems.length; i++) {
        const item = cartItems[i];

        // Get full product details from cache or current page
        const fullProduct = productCache[item.product_id] || availableProducts.find(p => p._id === item.product_id);

        const productPayload = {
          product_id: item.product_id,
          product_name: item.product_name,
          product_brand: fullProduct?.brand || '',
          product_global_code: fullProduct?.product_global_code || '',
          amazon_asin: fullProduct?.amazon_asin || '',
          dimensions: fullProduct?.dimensions || '',
          base_unit: fullProduct?.base_unit || 'adet',
          color: fullProduct?.color || '',
          category: fullProduct?.category || '',
          description: fullProduct?.description || '',
          quantity: item.quantity,
          unit: fullProduct?.base_unit || 'adet',
          budget: 0,
          currency: 'TRY',
          notes: '',
          line_number: i + 1
        };

        console.log(`Adding product ${i + 1}/${cartItems.length}:`, productPayload);

        const productResponse = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${requestId}/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(productPayload)
        });

        if (!productResponse.ok) {
          const errorData = await productResponse.json();
          console.error(`Failed to add product ${i + 1}:`, errorData);
          throw new Error(`Failed to add product: ${item.name}`);
        }

        const productResult = await productResponse.json();
        console.log(`Product ${i + 1} added successfully:`, productResult);
      }

      alert(`Request created successfully with ${cartItems.length} product(s)!`);

      // Clear cart after successful request creation
      await handleClearCart();

      // Close checkout modal
      setShowCheckout(false);

      // Navigate to Open RFQ dashboard
      onNavigate('/dashboard/user/inbox/open');
    } catch (error: any) {
      console.error('Error creating request:', error);
      alert(`Failed to create request: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Product columns for display
  const productColumns = [
    {
      key: 'image',
      title: 'Image',
      type: 'image' as const,
      sortable: false,
      editable: false,
      render: (value: any, item: any) => {
        if (item.image_gridfs_id) {
          const imageUrl = `http://localhost:5001/api/gridfs/${item.image_gridfs_id}`;
          return (
            <img
              src={imageUrl}
              alt={item.name}
              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
              }}
            />
          );
        }
        return (
          <div
            style={{
              width: '50px',
              height: '50px',
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#6b7280'
            }}
          >
            No Image
          </div>
        );
      }
    },
    { key: 'name', title: 'Name', type: 'text' as const, sortable: true, editable: false },
    { key: 'brand', title: 'Brand', type: 'text' as const, sortable: true, editable: false },
    {
      key: 'category',
      title: 'Category',
      type: 'text' as const,
      sortable: true,
      editable: false,
      render: (value: any) => {
        if (!value) return 'N/A';
        if (Array.isArray(value)) {
          return value.map((catId: string) => getCategoryName(catId, 'tr')).join(', ');
        }
        return getCategoryName(value, 'tr');
      }
    },
    { key: 'base_unit', title: 'Unit', type: 'text' as const, sortable: true, editable: false },
    { key: 'color', title: 'Color', type: 'text' as const, sortable: true, editable: false },
    { key: 'product_id', title: 'Product ID', type: 'text' as const, sortable: true, editable: false }
  ];

  // Checkout Modal
  const CheckoutModal = () => {
    if (!showCheckout) return null;

    return (
      <div className="cart-modal-overlay" onClick={() => setShowCheckout(false)}>
        <div className="cart-modal checkout-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cart-header">
            <h3>Checkout - Address Selection</h3>
            <button className="close-btn" onClick={() => setShowCheckout(false)}>×</button>
          </div>

          <div className="cart-content">
            <div className="checkout-section">
              <h4>Delivery Address</h4>
              {addresses.length === 0 ? (
                <p className="no-addresses">No addresses found. Please add an address first.</p>
              ) : (
                <select
                  className="address-select"
                  value={selectedDeliveryAddress}
                  onChange={(e) => setSelectedDeliveryAddress(e.target.value)}
                >
                  <option value="">Select delivery address...</option>
                  {addresses.map((addr) => (
                    <option key={addr._id} value={addr._id}>
                      {addr.street}, {addr.city} {addr.postal_code}, {addr.country} {addr.is_default ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="checkout-section">
              <h4>Invoice Address</h4>
              {addresses.length === 0 ? (
                <p className="no-addresses">No addresses found. Please add an address first.</p>
              ) : (
                <select
                  className="address-select"
                  value={selectedInvoiceAddress}
                  onChange={(e) => setSelectedInvoiceAddress(e.target.value)}
                >
                  <option value="">Select invoice address...</option>
                  {addresses.map((addr) => (
                    <option key={addr._id} value={addr._id}>
                      {addr.street}, {addr.city} {addr.postal_code}, {addr.country} {addr.is_default ? '(Default)' : ''}
                    </option>
                  ))}
                </select>
              )}

              <label className="same-as-delivery">
                <input
                  type="checkbox"
                  checked={selectedInvoiceAddress === selectedDeliveryAddress && selectedDeliveryAddress !== ''}
                  onChange={(e) => {
                    if (e.target.checked && selectedDeliveryAddress) {
                      setSelectedInvoiceAddress(selectedDeliveryAddress);
                    }
                  }}
                />
                Same as delivery address
              </label>
            </div>

            <div className="checkout-summary">
              <h4>Order Summary</h4>
              <p>{cartItems.length} item(s) in cart</p>
            </div>

            <div className="cart-actions">
              <button
                className="clear-btn"
                onClick={() => {
                  setShowCheckout(false);
                  setShowCart(true);
                }}
              >
                Back to Cart
              </button>
              <button
                className="submit-btn"
                onClick={handleSubmitRequest}
                disabled={isSubmitting || !selectedDeliveryAddress || !selectedInvoiceAddress || addresses.length === 0}
              >
                {isSubmitting ? 'Creating Request...' : 'Create Request'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Variant Selector Modal
  const VariantSelectorModal = () => {
    const [selectedVariant, setSelectedVariant] = useState<any>(null);

    if (!showVariantModal || !selectedProduct) return null;

    return (
      <div className="variant-modal-overlay" onClick={() => setShowVariantModal(false)}>
        <div className="variant-modal" onClick={(e) => e.stopPropagation()}>
          <div className="variant-modal-header">
            <h2>Select Variant</h2>
            <button
              className="variant-modal-close"
              onClick={() => setShowVariantModal(false)}
            >
              ×
            </button>
          </div>

          <div className="variant-modal-body">
            <div className="variant-product-info">
              <h3>{selectedProduct.name}</h3>
              <p className="variant-product-brand">{selectedProduct.brand}</p>
            </div>

            {loadingVariants ? (
              <div className="variant-loading">Loading variants...</div>
            ) : (
              <div className="variant-list">
                {productVariants.map((variant) => (
                  <div
                    key={variant._id}
                    className={`variant-option ${selectedVariant?._id === variant._id ? 'selected' : ''}`}
                    onClick={() => setSelectedVariant(variant)}
                  >
                    <div className="variant-details">
                      <div className="variant-attr">
                        <strong>Color:</strong> {variant.color || 'N/A'}
                      </div>
                      <div className="variant-attr">
                        <strong>Size:</strong> {variant.dimensions || 'N/A'}
                      </div>
                    </div>
                    {selectedVariant?._id === variant._id && (
                      <div className="variant-check">✓</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="variant-modal-footer">
            <button
              className="variant-cancel-btn"
              onClick={() => setShowVariantModal(false)}
            >
              Cancel
            </button>
            <button
              className="variant-add-btn"
              onClick={async () => {
                if (selectedVariant) {
                  await addProductToCart(selectedVariant);
                  setShowVariantModal(false);
                  setSelectedVariant(null);
                }
              }}
              disabled={!selectedVariant}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Cart component
  const CartModal = () => {
    if (!showCart) return null;

    return (
      <div className="cart-modal-overlay" onClick={() => setShowCart(false)}>
        <div className="cart-modal" onClick={(e) => e.stopPropagation()}>
          <div className="cart-header">
            <h3>Shopping Cart ({cartItems.length})</h3>
            <button className="close-btn" onClick={() => setShowCart(false)}>×</button>
          </div>

          <div className="cart-content">
            {cartItems.length === 0 ? (
              <p className="empty-cart">Your cart is empty</p>
            ) : (
              <>
                <div className="cart-items">
                  {cartItems.map((item, index) => {
                    // Get full product for image
                    const fullProduct = productCache[item.product_id] || availableProducts.find(p => p._id === item.product_id);
                    const imageId = fullProduct?.image_gridfs_id;

                    return (
                      <div key={item.product_id || item.product_name} className="cart-item">
                        <div className="item-info">
                          {imageId && (
                            <img
                              src={`http://localhost:5001/api/gridfs/${imageId}`}
                              alt={item.product_name}
                              className="item-image"
                            />
                          )}
                          <div>
                            <h4>{item.product_name}</h4>
                            {item.is_manual && <span className="manual-badge">Manual</span>}
                          </div>
                        </div>
                        <div className="item-controls">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleUpdateCartQuantity(item.product_id, parseInt(e.target.value))}
                            className="quantity-input"
                          />
                          <button
                            className="remove-btn"
                            onClick={() => handleRemoveFromCart(item.product_id)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="cart-actions">
                  <button className="clear-btn" onClick={handleClearCart}>
                    Clear Cart
                  </button>
                  <button
                    className="submit-btn"
                    onClick={() => {
                      setShowCart(false);
                      setShowCheckout(true);
                    }}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <DataDashboard
        title="Create Request"
        pageDescription="Choose products you to add to your cart"
        collection="marketplace_products"
        data={availableProducts}
        columns={productColumns}
        isLoading={isLoadingProducts}
        error={productsError}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        cartItemCount={cartItems.length}
        onDataChange={() => { }}
        onDataFetch={handleDataFetch}
        paginationInfo={paginationInfo}
        showAddButton={false}
        showSearch={true}
        showSelection={false}
        showExportButton={false}
        layoutType="user"
        cartButton={{
          label: `🛒 ${cartItems.length}`,
          onClick: () => onNavigate('/dashboard/user/cart'),
          className: `cart-btn ${cartItems.length > 0 ? 'has-items' : ''}`
        }}
        onRowAction={(product) => handleAddToCart(product)}
        rowActionLabel="Add to Cart"
        headerActions={[
          {
            key: 'add-manual-product',
            label: 'Add Manual Product',
            type: 'default',
            onClick: () => onNavigate('/dashboard/user/create-request/manual'),
            visible: true
          }
        ]}
      />

      <VariantSelectorModal />
      <CartModal />
      <CheckoutModal />

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
    </>
  );
};

export default CreateRequestDashboard;